/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { css, jsx } from '@emotion/react';

import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import HoverPopover from 'material-ui-popup-state/HoverPopover';
import PopupState, { bindPopover, bindHover } from 'material-ui-popup-state';

import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import AppContext from '../../contexts/app';
import MoreMenuItem from '../common/MoreMenuItem';
import { track } from '../../utils/track';
import { showPicker as showGooglePicker } from '../../utils/google';
import { showPicker as showOneDrivePicker } from '../../utils/onedrive';
import { IS_SINGLE_TENENT_MODE } from '../../constants';
import { useUserQuery } from '../../actions/UserActions';

let timeout;

function wrappedBindHover(popupState) {
  const { onMouseLeave, ...rest } = bindHover(popupState);

  function _onMouseLeave(e) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      onMouseLeave(e);
    }, 400);
  }

  return {
    ...rest,
    onMouseLeave: _onMouseLeave,
  };
}

export default function UploadMenu({ variant = 'large' }) {
  const { t } = useTranslation();
  const { setUploadDialog } = useContext(AppContext);
  const { user } = useUserQuery();

  async function onItemClick(value) {
    track('upload_buttons_click', { type: value });
    if (value === 'google_drive') {
      try {
        await showGooglePicker(
          function (ok, payload) {
            if (ok) {
              const { fileId, fileName, oauthToken } = payload;
              console.log({ payload });
              setUploadDialog({
                open: true,
                type: 'google_drive',
                data: { fileId, fileName, oauthToken },
              });
            }
          },
          undefined,
          (formData, fileName, fileType) => {}
        );
      } catch (e) {
        console.log('SHOW PICKER ERROR', e);
      }
      return;
    } else if (value === 'onedrive') {
      try {
        const { url, name } = await showOneDrivePicker();
        setUploadDialog({
          open: true,
          type: 'onedrive',
          data: { url, fileName: name },
        });
      } catch (e) {}
      return;
    }
    setUploadDialog({ open: true, type: value });
  }

  const buttons = [
    {
      icon: UploadFileOutlinedIcon,
      label: t('Ladda upp fil'),
      id: 'upload-file-button',
      value: 'file',
    },
    {
      icon: MicNoneOutlinedIcon,
      label: t('Diktafon'),
      value: 'record',
      id: 'record-audio-button',
    },
  ];

  if (user?.workspace?.allow_meeting_bot) {
    buttons.push({
      icon: VideocamOutlinedIcon,
      label: t('Spela in webbmöte'),
      value: 'liveMeeting',
      id: 'live-meeting-button',
    });
  }

  if (!IS_SINGLE_TENENT_MODE) {
    buttons.push(
      {
        icon: PhoneOutlinedIcon,
        label: t('Telefonsamtal'),
        value: 'phone',
        id: 'call-button',
      },
      {
        icon: LinkOutlinedIcon,
        label: t('Importera URL'),
        value: 'url',
        id: 'import-url-button',
      }
    );
  }

  return (
    <PopupState variant="popover" popupId="upload-menu-popover">
      {(popupState) => (
        <>
          {variant === 'large' ? (
            <Button
              {...wrappedBindHover(popupState)}
              variant="contained"
              color="primary"
              size="large"
              id="upload-button-popover-cta"
              css={css`
                background: #4c53fb;
                width: 150px;
                height: 105px;
                font-size: 15px;
                font-weight: 600;
                line-height: 22.5px;
                flex-direction: column;
                align-items: flex-start;
                text-wrap: nowrap;
                border-radius: 8px;
                padding: 16px;
                gap: 8px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  background: #ffffff29;
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  justify-content: center;
                  align-items: center;
                `}
              >
                <AddOutlinedIcon />
              </div>
              {t('Ladda upp')}
            </Button>
          ) : (
            <IconButton
              {...wrappedBindHover(popupState)}
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 48px;
                max-width: 48px;
                min-height: 48px;
                max-height: 48px;
                border-radius: 0.5rem;
                background: linear-gradient(
                  135deg,
                  #2028fa 0%,
                  rgba(32, 40, 250, 0.54) 100%
                );
                cursor: pointer;
                color: white;
              `}
            >
              <AddOutlinedIcon color="inherit" />
            </IconButton>
          )}
          <HoverPopover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onMouseEnter={() => {
              if (timeout) {
                clearTimeout(timeout);
              }
            }}
          >
            {buttons.map(({ icon: Icon, label, id, value, children }) => {
              if (children) {
                return (
                  <MoreMenuItem
                    css={css`
                      height: 48px;
                      border-radius: 8px;
                      margin: 8px 0;
                      font-weight: 600;
                      font-size: 15px;
                      &:hover {
                        background: #2028fa14;
                        color: #2028fa;
                      }
                    `}
                    label={
                      <>
                        <span>{label}</span>
                        <Icon sx={{ ml: 'auto' }} />
                      </>
                    }
                    id={id}
                    key={id}
                    MenuProps={{
                      MenuListProps: {
                        sx: {
                          minWidth: '220px',
                        },
                      },
                    }}
                  >
                    {children.map(({ value, label, icon: Icon, svg }) => (
                      <MenuItem
                        key={value}
                        onClick={() => onItemClick(value)}
                        css={css`
                          height: 40px;
                          font-weight: 600;
                          font-size: 15px;
                          &:hover {
                            background: #2028fa14;
                            color: #2028fa;
                          }
                        `}
                      >
                        {svg ? (
                          <img
                            src={svg}
                            alt={label}
                            width="32"
                            height="32"
                            css={css`
                              margin-right: 16px;
                            `}
                          />
                        ) : (
                          <Icon sx={{ ml: 0.5, mr: 2.5 }} />
                        )}
                        {label}
                      </MenuItem>
                    ))}
                  </MoreMenuItem>
                );
              }
              return (
                <MenuItem
                  key={id}
                  onClick={() => onItemClick(value)}
                  css={css`
                    height: 48px;
                    border-radius: 8px;
                    margin: 8px 0;
                    font-weight: 600;
                    font-size: 15px;
                    &:hover {
                      background: #2028fa14;
                      color: #2028fa;
                    }
                  `}
                >
                  {label}
                  <Icon sx={{ pl: 1, ml: 'auto' }} />
                </MenuItem>
              );
            })}
          </HoverPopover>
        </>
      )}
    </PopupState>
  );
}
